import { FILTER_KEYS, TRIAGE_FLAGS, type FilterKey, type TicketFilter, type TriageFlag } from "@server/reporting/metrics";

export const TRIAGE_FLAG_LABELS: Record<TriageFlag, string> = {
  overridden: "Raised despite a \"no ticket\" verdict",
  typeChanged: "Type changed by the person",
};

export const FILTER_LABELS: Record<FilterKey, string> = {
  businessUnit: "Business Unit",
  severity: "Severity",
  raiser: "Ticket raiser",
  feature: "Feature",
  company: "Company",
  team: "ENG team",
  engineer: "Engineer",
  status: "Ticket status",
  ticketType: "Ticket type",
  huddle: "CS huddle",
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;

// The filters live in the page address (repeated parameters, for example ?businessUnit=Scale&businessUnit=Growth), so a filtered view can be shared.
export function filterFromParams(params: URLSearchParams): TicketFilter {
  const day = (name: string): string | undefined => {
    const value = params.get(name);
    return value && DAY.test(value) ? value : undefined;
  };
  const values: TicketFilter["values"] = {};
  for (const key of FILTER_KEYS) {
    const selected = params.getAll(key);
    if (selected.length > 0) {
      values[key] = selected;
    }
  }
  return { from: day("from"), to: day("to"), values, triage: TRIAGE_FLAGS.find((flag) => flag === params.get("triage")) };
}

export function paramsFromFilter(filter: TicketFilter): URLSearchParams {
  const params = new URLSearchParams();
  if (filter.from) {
    params.set("from", filter.from);
  }
  if (filter.to) {
    params.set("to", filter.to);
  }
  if (filter.triage) {
    params.set("triage", filter.triage);
  }
  for (const key of FILTER_KEYS) {
    for (const value of filter.values[key] ?? []) {
      params.append(key, value);
    }
  }
  return params;
}

export const isFiltered = (filter: TicketFilter): boolean =>
  Boolean(filter.from || filter.to || filter.triage) || FILTER_KEYS.some((key) => (filter.values[key]?.length ?? 0) > 0);

// The address of the tickets behind a figure: the filters in force now, with one more applied (a value of one filter, or a triage flag).
export function drillSearch(current: URLSearchParams, change: { key: FilterKey; value: string } | { triage: TriageFlag }): string {
  const filter = filterFromParams(current);
  const next: TicketFilter = "triage" in change ? { ...filter, triage: change.triage } : { ...filter, values: { ...filter.values, [change.key]: [change.value] } };
  return paramsFromFilter(next).toString();
}
